import React, { FC } from 'react'
import {
  Space,
  Box,
  muiBankTheme,
  TextHeading,
  TextBody,
  Link,
  useMediaSize,
} from '@ally/metronome-ui'
import logRocket from 'logrocket'
import styled from 'styled-components'
import useMboxCall from '@ally/use-mbox-call'
import {
  AdobeResponse,
  AdobeTileContent,
  AdobeTile,
  MboxResponseObj,
} from '../types'
import { validateObjKeys, validateTiles } from '../utils'

const OfferTile = styled(Box)`
  margin-bottom: ${muiBankTheme.space.sm};

  &:last-of-type {
    margin-bottom: 0;
  }
`

const OfferTileContent = styled(Box)`
  display: flex;
  align-items: center;
  padding: ${muiBankTheme.space.sm};
  border: 1px dashed ${muiBankTheme.colors['slate-3']};
  border-radius: 4px;
`

const OfferTileTitle = styled(TextHeading)`
  text-transform: uppercase;
  margin-bottom: ${muiBankTheme.space.xs};
`

const OfferTileText = styled(TextBody)`
  flex: 2;
  margin-right: ${muiBankTheme.space.sm};
`

export const GhostTiles: FC = () => {
  const mboxResponse = useMboxCall('ally-dashboard-ghost-tiles', [])
    .mboxData as AdobeResponse<AdobeTileContent>

  const ghostTiles = (): AdobeResponse<AdobeTileContent> | [] => {
    if (Array.isArray(mboxResponse)) {
      return mboxResponse
    }
    logRocket.captureMessage('Adobe response invalid format', {
      tags: {
        page: 'dashboard',
      },
      extra: {
        response: mboxResponse,
      },
    })
    return []
  }

  const mboxSetJson = ghostTiles().filter(
    item => item.action === 'setJson',
  )[0] as MboxResponseObj<AdobeTileContent>
  const requiredContentKeys = [
    'description',
    'offerType',
    'tiles',
    'title',
    '_id',
  ]
  const requiredTileKeys = [
    'ariaText',
    'title',
    'description',
    'ctaLabel',
    'ctaHref',
  ]
  const content =
    ghostTiles().length &&
    mboxSetJson.content &&
    validateObjKeys(mboxSetJson.content[0], requiredContentKeys) &&
    validateTiles(mboxSetJson.content[0].tiles, requiredTileKeys) &&
    mboxSetJson.content[0]

  const isXlOnly = useMediaSize('XlOnly')

  return (
    <>
      {isXlOnly && content ? (
        <Box
          my="md"
          aria-label={content.title}
          data-track-mbox="ally-dashboard-ghost-tiles"
          // eslint-disable-next-line no-underscore-dangle
          data-track-offerid={content._id}
        >
          <Space mb="sm">
            <Space mb="xs">
              <TextHeading tag="h3" size="xs">
                More From Ally
              </TextHeading>
            </Space>
            <TextBody tag="p" size="sm">
              {content.description}
            </TextBody>
          </Space>

          {content.tiles.map((tile: AdobeTile) => {
            const { ariaText, title, description, ctaLabel, ctaHref } = tile
            return (
              <OfferTile>
                <OfferTileTitle tag="h4" size="xs" aria-label={ariaText}>
                  {title}
                </OfferTileTitle>
                <OfferTileContent>
                  <OfferTileText tag="span" size="sm">
                    {description}
                  </OfferTileText>
                  <Link
                    variant="outline"
                    text={ctaLabel}
                    to={ctaHref}
                    size="sm"
                  />
                </OfferTileContent>
              </OfferTile>
            )
          })}
        </Box>
      ) : null}
    </>
  )
}
